import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  brainLegAvailabilitySummary,
  buildModelBucketCuration,
  engineLegsForModel,
  isBrainModelSelected,
  isConfiguredModelRetired,
  parseModelCostTiers,
  resolveBrainCandidateChain,
  type BrainPriorityList,
  type EnterpriseAdminBundleRawModel,
  type ModelBucketCuration,
  type ModelCatalogEntry,
  type ModelSelection,
} from "@dosia/shared";
import { apiCall } from "../../lib/api-client.js";
import { ApiError } from "../../lib/api-types.js";
import { orgModelBucketCurationPath, publicModelGovernancePath } from "../../lib/api-paths.js";
import { moveIndex } from "../../lib/platform-tool-curation-view.js";
import { useApiQuery } from "../../lib/useApiQuery.js";
import { useRetiredMarksCatalog } from "../../lib/useRetiredMarksCatalog.js";
import { InfoBanner } from "../../components/ErrorBanner.js";
import { PriorityListEditor } from "../../components/PriorityListEditor.js";
import {
  anthropicLegBadge,
  priorityBadgeLabel,
  type PriorityBadge,
} from "../../lib/priority-badges.js";

export interface CloudTaskModelListCardProps {
  orgCode: string;
  token: string;
  rawModels: EnterpriseAdminBundleRawModel[];
  orgSelection: ModelSelection | null;
}

function baseId(modelId: string): string {
  return modelId.replace(/\[[^\]]*\]/g, "");
}

function errText(err: unknown): string {
  return err instanceof ApiError ? err.message : "操作失败，请稍后重试。";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** GET / PUT 响应里的组织清单：过唯一构建器（坏行丢弃、空档归一为删键）。旧键不读——这张卡只管 `standard`。 */
function curationFromResponse(raw: unknown): ModelBucketCuration {
  return buildModelBucketCuration(isRecord(raw) ? raw.modelBucketCuration : undefined);
}

/** 组织在「标准」档整份替换的模型 id；没换返回 null（= 跟随平台）。 */
function standardModelIds(curation: ModelBucketCuration): string[] | null {
  const ids = curation.standard?.candidates.map((candidate) => candidate.model);
  return ids && ids.length > 0 ? ids : null;
}

/**
 * PUT 体：只换 `standard`，组织已有的 `economy` / `premium` / `taskTypeTiers` 原样带回（写库按这四键整体替换，
 * 漏带 = 清掉）。`standard` 传 null = 恢复跟随，不带该键。
 */
function curationPutBody(
  saved: ModelBucketCuration,
  standard: readonly string[] | null,
): { modelBucketCuration: ModelBucketCuration } {
  return {
    modelBucketCuration: {
      ...(saved.economy ? { economy: saved.economy } : {}),
      ...(standard ? { standard: { candidates: standard.map((model) => ({ model })) } } : {}),
      ...(saved.premium ? { premium: saved.premium } : {}),
      ...(saved.taskTypeTiers ? { taskTypeTiers: saved.taskTypeTiers } : {}),
    },
  };
}

function violationMessages(details: unknown): string | null {
  if (!Array.isArray(details)) return null;
  const messages: string[] = [];
  for (const item of details) {
    if (isRecord(item) && typeof item.message === "string" && item.message.trim()) {
      messages.push(item.message);
    }
  }
  return messages.length > 0 ? messages.join("；") : null;
}

function rowsForBase(
  models: readonly EnterpriseAdminBundleRawModel[],
  modelId: string,
): EnterpriseAdminBundleRawModel[] {
  const base = baseId(modelId);
  return models.filter((row) => baseId(row.modelId) === base);
}

function displayNameOf(
  models: readonly EnterpriseAdminBundleRawModel[],
  modelId: string,
): string {
  const rows = rowsForBase(models, modelId);
  const named = rows.find((row) => row.displayName)?.displayName;
  return named || baseId(modelId);
}

function toCatalogRow(row: EnterpriseAdminBundleRawModel): ModelCatalogEntry {
  return {
    modelId: row.modelId,
    protocol: row.protocol,
    // Claude 主脑线的唯一判据（isBrainPoolEntry：anthropic 行且 translated === false）——漏拷则组织的
    // brainModels 圈定在这张卡上失效（所有模型都落工具线）。
    ...(typeof row.translated === "boolean" ? { translated: row.translated } : {}),
    vendor: row.vendor,
    family: row.family,
    chosenGroup: row.chosenGroup,
    availableGroups: row.availableGroups,
    capabilities: row.capabilities,
    displayName: row.displayName,
    available: row.available,
    unavailableReason: row.unavailableReason,
  };
}

/**
 * 这张卡怎么看一个模型（N-121 批次 D 审查 MINOR-3，与值班表写闸同口径，单源 shared `engineLegsForModel`）：
 * 有 Claude 引擎腿 → 徽标按 anthropic 行原生性、可用性看 Claude 腿；只有 pi 腿（GPT 等）→ 徽标「pi 引擎」、
 * 可用性看 pi 腿；两条都没有（存量配置）→ 沿用 anthropic 行徽标与 Claude 腿可用性（写闸不会再放进来）。
 */
function engineViewOf(rows: readonly EnterpriseAdminBundleRawModel[], modelId: string): {
  badge: PriorityBadge;
  chain: "claude" | "pi";
  legs: { claude: boolean; pi: boolean };
} {
  const catalogRows = rows.map(toCatalogRow);
  const legs = engineLegsForModel(catalogRows, modelId);
  const anthropic = rows.find((row) => row.protocol === "anthropic_messages");
  if (!legs.claude && legs.pi) return { badge: "pi", chain: "pi", legs };
  return { badge: anthropicLegBadge(anthropic) ?? "unknown", chain: "claude", legs };
}

/**
 * 「添加模型」的候选（与值班表写闸同口径）：本组织 bundle 里 Claude 引擎腿或 pi 腿有任一、且在组织圈定内
 * （`isBrainModelSelected`，与写闸同一判定）的模型，按 base id 去重、保持清单顺序。
 */
export function cloudTaskModelCandidates(
  rawModels: readonly EnterpriseAdminBundleRawModel[],
  orgSelection: ModelSelection | null,
): Array<{ modelId: string; displayName: string; badge: PriorityBadge }> {
  const catalogRows = rawModels.map(toCatalogRow);
  const out: Array<{ modelId: string; displayName: string; badge: PriorityBadge }> = [];
  for (const base of new Set(rawModels.map((row) => baseId(row.modelId)))) {
    const rows = rowsForBase(rawModels, base);
    const view = engineViewOf(rows, base);
    if (!view.legs.claude && !view.legs.pi) continue;
    if (orgSelection && !isBrainModelSelected(base, catalogRows, orgSelection)) continue;
    out.push({ modelId: base, displayName: displayNameOf(rawModels, base), badge: view.badge });
  }
  return out;
}

export function CloudTaskModelListCard({
  orgCode,
  token,
  rawModels,
  orgSelection,
}: CloudTaskModelListCardProps): JSX.Element | null {
  const { t } = useTranslation();
  // GET 返回 { modelBucketCuration, platformCostTiers }；按 unknown 收，边界处过构建器 / guard。
  const curationQ = useApiQuery<unknown>(
    orgModelBucketCurationPath(orgCode),
    [orgCode],
  );
  const govQ = useApiQuery<{ data?: { brainPriority?: BrainPriorityList } }>(
    publicModelGovernancePath(),
    [],
  );

  const [override, setOverride] = useState<string[] | null>(null);
  // 组织清单最近一次已知的完整内容（读到的 / 保存后服务端回的），保存时其余三键据此原样带回。
  const [saved, setSaved] = useState<ModelBucketCuration>({});
  const [hydrated, setHydrated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const liveCatalog = useRetiredMarksCatalog(orgCode);

  useEffect(() => {
    if (curationQ.data == null || hydrated) return;
    const curation = curationFromResponse(curationQ.data);
    setSaved(curation);
    setOverride(standardModelIds(curation));
    setHydrated(true);
  }, [curationQ.data, hydrated]);

  // 跟随态预览的来源：平台「标准」档配置了就用它（与服务端选模型同一份清单）；没配才回落平台默认主脑优先级。
  const platformStandard = useMemo(
    () => parseModelCostTiers(isRecord(curationQ.data) ? curationQ.data.platformCostTiers : undefined)?.tiers.standard ?? [],
    [curationQ.data],
  );
  const followsStandardTier = platformStandard.length > 0;

  const platformChain = useMemo(
    () => resolveBrainCandidateChain({
      priority: followsStandardTier
        ? { entries: platformStandard, editedAt: "", editedBy: "" }
        : govQ.data?.data?.brainPriority,
      catalog: rawModels.map(toCatalogRow),
      orgSelection,
      chain: "claude",
    }),
    [followsStandardTier, platformStandard, govQ.data, rawModels, orgSelection],
  );
  const platformIds = platformChain.candidates.map((c) => c.model);
  const following = override === null;
  const effective = override ?? platformIds;
  // 企业覆盖清单里已从网关下线的行（N-88）：判据与下发同源（shared isConfiguredModelRetired，下发时
  // resolveModelBucketCurationForDelivery 剔的就是这些）；没有 live 清单一个都不标。跟随态的顺序来自平台，
  // 不是本卡存的配置，不在这里标。
  const isRetired = (modelId: string): boolean =>
    liveCatalog !== undefined && isConfiguredModelRetired(modelId, liveCatalog);
  const retiredIds = override ? override.filter(isRetired) : [];
  const retiredSet = new Set(retiredIds);

  const items = effective.map((modelId) => {
    const rows = rowsForBase(rawModels, modelId);
    const anthropic = rows.find((row) => row.protocol === "anthropic_messages");
    const view = engineViewOf(rows, modelId);
    return {
      modelId,
      name: anthropic?.displayName || rows.find((row) => row.displayName)?.displayName || modelId,
      badge: view.badge,
      // 可用性看这个模型实际会走的那条腿（GPT 看 pi 腿），不再一律按 Claude 腿汇总成「目录未声明」。
      availability: brainLegAvailabilitySummary(rows.map(toCatalogRow), view.chain).label ?? "目录未声明",
      ...(retiredSet.has(modelId) ? { retiredLabel: t("admin.cloudTaskModelList.retiredBadge") } : {}),
    };
  });

  const effectiveSet = new Set(effective);
  const options = cloudTaskModelCandidates(rawModels, orgSelection)
    .filter((candidate) => !effectiveSet.has(candidate.modelId))
    .map((candidate) => ({
      modelId: candidate.modelId,
      label: candidate.displayName + " · " + priorityBadgeLabel(candidate.badge),
    }));

  const skippedNames = following
    ? platformChain.skipped
      .filter((item) => item.unavailableReason === "wrong_chain" || item.unavailableReason === "no_brain_leg")
      .map((item) => displayNameOf(rawModels, item.modelId))
    : [];

  const mutate = (apply: (current: string[]) => string[]): void => {
    setSavedAt(null);
    setOverride((prev) => apply(prev ?? platformIds));
  };

  const save = async (): Promise<void> => {
    if (override === null) return;
    setSaving(true);
    setError(null);
    try {
      const result = await apiCall<unknown>(
        "PUT",
        orgModelBucketCurationPath(orgCode),
        { token, body: curationPutBody(saved, override) },
      );
      const next = curationFromResponse(result);
      setSaved(next);
      setOverride(standardModelIds(next) ?? override);
      setSavedAt(new Date().toLocaleTimeString());
    } catch (err) {
      const fromViolations = err instanceof ApiError ? violationMessages(err.details) : null;
      setError(fromViolations ?? errText(err));
    } finally {
      setSaving(false);
    }
  };

  const restoreFollow = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const result = await apiCall<unknown>(
        "PUT",
        orgModelBucketCurationPath(orgCode),
        { token, body: curationPutBody(saved, null) },
      );
      setSaved(curationFromResponse(result));
      setOverride(null);
      setSavedAt(new Date().toLocaleTimeString());
    } catch (err) {
      const fromViolations = err instanceof ApiError ? violationMessages(err.details) : null;
      setError(fromViolations ?? errText(err));
    } finally {
      setSaving(false);
    }
  };

  if (curationQ.data == null || !hydrated) return null;
  // 跟随态等平台顺序请求落地，避免空列表闪一下；覆盖清单与「跟随平台标准档」都不依赖 govQ，失败也照常渲染。
  if (override === null && !followsStandardTier && govQ.loading) return null;
  const govHasPayload = isRecord(govQ.data?.data);
  const govUnavailable = !followsStandardTier && (Boolean(govQ.error) || (!govQ.loading && !govHasPayload));

  return (
    <div className="space-y-3">
      {govUnavailable && following ? (
        <div role="status">
          <InfoBanner>{t("admin.cloudTaskModelList.govUnavailable")}</InfoBanner>
        </div>
      ) : null}
      <PriorityListEditor
        title={t("admin.cloudTaskModelList.title")}
        blurb={t("admin.cloudTaskModelList.hint")}
        headerExtra={(
          <>
            <span className="rounded bg-dosia-accent/15 px-2 py-0.5 text-xs text-dosia-accent">
              {following ? t("admin.cloudTaskModelList.badgeFollow") : t("admin.cloudTaskModelList.badgeOverride")}
            </span>
            {following ? null : (
              <button
                type="button"
                className="text-xs text-dosia-accent underline"
                aria-label="restore-follow"
                onClick={() => { void restoreFollow(); }}
              >
                {t("admin.cloudTaskModelList.restoreFollow")}
              </button>
            )}
          </>
        )}
        notice={retiredIds.length > 0 ? (
          <div
            data-testid="cloud-task-retired-banner"
            className="flex flex-wrap items-center justify-between gap-2 rounded border border-dosia-warning/40 bg-dosia-warning/10 px-3 py-2 text-xs text-dosia-warning"
          >
            <span>
              <span className="font-medium">
                {t("admin.cloudTaskModelList.retiredBanner", { n: retiredIds.length })}
              </span>
              {retiredIds.join("、")}
            </span>
            {/* 剔空保护：全是下线 id 时不给一键移除（清单剔空 = 回到跟随平台，会翻转含义）。 */}
            {effective.length > retiredIds.length ? (
              <button
                type="button"
                className="hover:underline"
                onClick={() => mutate((current) => current.filter((modelId) => !isRetired(modelId)))}
              >
                {t("admin.cloudTaskModelList.retiredRemove")}
              </button>
            ) : null}
          </div>
        ) : null}
        items={items}
        options={options}
        onMove={(index, delta) => {
          mutate((current) => moveIndex(current, index, delta));
        }}
        onRemove={(index) => {
          mutate((current) => current.filter((_, itemIndex) => itemIndex !== index));
        }}
        onAdd={(modelId) => {
          mutate((current) => current.includes(modelId) ? current : [...current, modelId]);
        }}
        onSave={() => { void save(); }}
        saving={saving}
        savedAt={savedAt}
        error={error}
        footerNote={
          skippedNames.length > 0
            ? t("admin.cloudTaskModelList.skippedNote", { names: skippedNames.join(" · ") })
            : undefined
        }
        addDisabled={following && !govUnavailable}
        saveDisabled={following}
        addLabel={t("admin.cloudTaskModelList.add")}
        saveLabel={t("admin.cloudTaskModelList.save")}
        emptyLabel={following && !govUnavailable ? t("admin.cloudTaskModelList.platformEmpty") : ""}
        idleNote={following ? t("admin.cloudTaskModelList.unchanged") : undefined}
        savedNote={savedAt ? t("admin.cloudTaskModelList.saved", { time: savedAt }) : undefined}
        ariaPrefix="云端任务模型"
        testId="cloud-task-model-list"
      />
    </div>
  );
}
